'use client'

import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
} from '@headlessui/react'
import {
  Bars3Icon,
  HomeIcon,
  UsersIcon,
  XMarkIcon,
  ShoppingCartIcon,
  TagIcon,
  CubeIcon,
  FireIcon,
  TrashIcon,
  ChartBarIcon,
  ArrowRightOnRectangleIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { logoutUser, getCurrentUser } from '../services/apiService'
import { startLogoutTimer, resetLogoutTimer, clearLogoutTimer } from '../utils/authUtils'
import { useLanguage } from '../contexts/LanguageContext'

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function SidebarLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [userRole, setUserRole] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Admin navigation grouped by section
  const adminSections = useMemo(() => ([
    {
      key: 'main',
      label: t('sidebar.sections.main'),
      items: [
        { key: 'dashboard', href: '/', icon: HomeIcon, name: t('sidebar.navigation.dashboard.name') },
        { key: 'statistics', href: '/statistics', icon: ChartBarIcon, name: t('sidebar.navigation.statistics.name') },
      ],
    },
    {
      key: 'people',
      label: t('sidebar.sections.people'),
      items: [
        { key: 'users', href: '/users', icon: UsersIcon, name: t('sidebar.navigation.users.name') },
      ],
    },
    {
      key: 'catalog',
      label: t('sidebar.sections.catalog'),
      items: [
        { key: 'products', href: '/products', icon: CubeIcon, name: t('sidebar.navigation.products.name') },
        { key: 'bestsellers', href: '/bestsellers', icon: FireIcon, name: t('sidebar.navigation.bestsellers.name') },
        { key: 'discounts', href: '/discounts', icon: TagIcon, name: t('sidebar.navigation.discounts.name') },
        { key: 'deletedProducts', href: '/products/deleted', icon: TrashIcon, name: t('sidebar.navigation.deletedProducts.name') },
      ],
    },
    {
      key: 'sales',
      label: t('sidebar.sections.sales'),
      items: [
        { key: 'orders', href: '/orders', icon: ShoppingCartIcon, name: t('sidebar.navigation.orders.name') },
      ],
    },
  ]), [t]);

  const clientSections = useMemo(() => ([
    {
      key: 'main',
      label: t('sidebar.sections.main'),
      items: [
        { key: 'clientDashboard', href: '/', icon: HomeIcon, name: t('sidebar.navigation.clientDashboard.name') },
        { key: 'clientProducts', href: '/products', icon: CubeIcon, name: t('sidebar.navigation.clientProducts.name') },
      ],
    },
  ]), [t]);

  const isClient = userRole === 3 || userRole === '68cbb0c8ca57238aedb188bc' ||
                   (typeof userRole === 'string' && userRole.includes('68cbb0c8ca57238aedb188bc'));

  const sections = isClient ? clientSections : adminSections;

  // Current page name for the top-bar breadcrumb
  const currentItem = useMemo(() => {
    for (const s of sections) {
      const found = s.items.find((it) => it.href === currentPath);
      if (found) return found;
    }
    return null;
  }, [sections, currentPath]);

  const handleNavigation = (href) => {
    setCurrentPath(href);
    navigate(href);
    setSidebarOpen(false);
  };

  const handleLogout = () => {
    clearLogoutTimer();
    logoutUser();
    navigate('/login');
  };

  useEffect(() => {
    const fetchUserInfo = () => {
      try {
        const userData = getCurrentUser();
        if (!userData) {
          // Don't assume admin when identity is unknown — fail closed.
          setUserRole(null);
          return;
        }
        setCurrentUser(userData);
        let roleNumber;
        if (userData.role?.number) roleNumber = userData.role.number;
        else if (userData.role?.id) roleNumber = userData.role.id;
        else roleNumber = userData.role;
        setUserRole(roleNumber);
      } catch (error) {
        console.error('Error fetching user info in SidebarLayout:', error);
        setUserRole(null);
      }
    };

    fetchUserInfo();
    startLogoutTimer(handleLogout);

    const resetTimer = () => resetLogoutTimer(handleLogout);
    window.addEventListener('mousemove', resetTimer);
    window.addEventListener('keypress', resetTimer);
    window.addEventListener('click', resetTimer);
    window.addEventListener('scroll', resetTimer);

    return () => {
      clearLogoutTimer();
      window.removeEventListener('mousemove', resetTimer);
      window.removeEventListener('keypress', resetTimer);
      window.removeEventListener('click', resetTimer);
      window.removeEventListener('scroll', resetTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const userDisplayName =
    currentUser?.name || currentUser?.firstName ||
    (isClient ? t('sidebar.userMenu.client') : t('sidebar.userMenu.adminUser'));
  const userInitial = (
    currentUser?.firstName || currentUser?.name || userDisplayName || ''
  ).charAt(0).toUpperCase();
  const userRoleLabel = isClient ? t('sidebar.userMenu.client') : t('sidebar.userMenu.adminUser');

  // Shared sidebar content — used by both the mobile drawer and desktop column
  const sidebarContent = (
    <div className="flex grow flex-col overflow-y-auto bg-white border-r border-gray-200/70">
      {/* Brand */}
      <div className="flex items-center gap-3 px-5 h-20 border-b border-gray-100">
        <img
          alt="Dreirosen"
          src="/images/resized_newlogo.webp"
          className="h-14 w-14 object-contain"
        />
        <span className="text-base font-semibold tracking-tight text-gray-900">
          {t('sidebar.brand')}
        </span>
      </div>

      {/* Sections */}
      <nav className="flex-1 px-3 py-4 space-y-5">
        {sections.map((section) => (
          <div key={section.key}>
            <h3 className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
              {section.label}
            </h3>
            <ul className="space-y-0.5">
              {section.items.map((item) => {
                const active = item.href === currentPath;
                return (
                  <li key={item.key}>
                    <a
                      href={item.href}
                      onClick={(e) => {
                        e.preventDefault();
                        handleNavigation(item.href);
                      }}
                      className={classNames(
                        'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150',
                        active
                          ? 'bg-indigo-50 text-indigo-700'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900',
                      )}
                    >
                      {/* Active accent bar */}
                      {active && (
                        <span
                          aria-hidden="true"
                          className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-1 rounded-r-full bg-gradient-to-b from-indigo-500 to-purple-600"
                        />
                      )}
                      <item.icon
                        aria-hidden="true"
                        className={classNames(
                          'h-5 w-5 shrink-0 transition-colors',
                          active ? 'text-indigo-600' : 'text-gray-400 group-hover:text-gray-600',
                        )}
                      />
                      <span className="truncate">{item.name}</span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* User card / sign out */}
      <div className="border-t border-gray-100 p-3">
        <div className="flex items-center gap-3 px-2 py-2 rounded-lg">
          <div className="flex-none size-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-sm font-semibold shadow-sm">
            {userInitial}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 truncate">{userDisplayName}</p>
            <p className="text-xs text-gray-500 truncate">{userRoleLabel}</p>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            title={t('sidebar.userMenu.signOut')}
            aria-label={t('sidebar.userMenu.signOut')}
            className="flex-none p-2 rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
          >
            <ArrowRightOnRectangleIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        {/* Mobile drawer */}
        <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
          />

          <div className="fixed inset-0 flex">
            <DialogPanel
              transition
              className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
            >
              <TransitionChild>
                <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                  <button
                    type="button"
                    onClick={() => setSidebarOpen(false)}
                    className="-m-2.5 p-2.5 bg-white rounded-full shadow-md hover:bg-gray-50 transition-all duration-200"
                  >
                    <span className="sr-only">{t('sidebar.accessibility.closeSidebar')}</span>
                    <XMarkIcon aria-hidden="true" className="size-5 text-gray-600" />
                  </button>
                </div>
              </TransitionChild>

              {sidebarContent}
            </DialogPanel>
          </div>
        </Dialog>

        {/* Desktop sidebar */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-64 lg:flex-col">
          {sidebarContent}
        </div>

        <div className="lg:pl-64">
          {/* Top bar */}
          <div className="sticky top-0 z-40 flex h-14 shrink-0 items-center gap-x-4 border-b border-gray-200/70 bg-white/85 backdrop-blur-md px-4 sm:px-6 lg:px-8">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="-m-2.5 p-2.5 text-gray-600 lg:hidden hover:bg-gray-100 rounded-lg transition-colors duration-200"
            >
              <span className="sr-only">{t('sidebar.accessibility.openSidebar')}</span>
              <Bars3Icon aria-hidden="true" className="size-5" />
            </button>

            <div aria-hidden="true" className="h-5 w-px bg-gray-200 lg:hidden" />

            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="flex-1 min-w-0">
              <ol className="flex items-center gap-1.5 text-sm text-gray-500">
                <li className="hidden sm:inline-flex">
                  <span className="text-gray-400">{t('sidebar.brand')}</span>
                </li>
                {currentItem && (
                  <>
                    <li className="hidden sm:inline-flex">
                      <ChevronRightIcon className="h-4 w-4 text-gray-300" aria-hidden="true" />
                    </li>
                    <li className="inline-flex">
                      <span className="font-semibold text-gray-900 truncate">{currentItem.name}</span>
                    </li>
                  </>
                )}
              </ol>
            </nav>

            {/* Account chip */}
            <Menu as="div" className="relative">
              <MenuButton className="flex items-center gap-2 -m-1.5 p-1.5 rounded-lg hover:bg-gray-100 transition-colors duration-200">
                <span className="sr-only">{t('sidebar.accessibility.openUserMenu')}</span>
                <div className="size-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-sm font-semibold shadow-sm">
                  {userInitial}
                </div>
                <span className="hidden sm:flex sm:items-center sm:gap-1">
                  <span aria-hidden="true" className="text-sm font-medium text-gray-800 truncate max-w-[10rem]">
                    {userDisplayName}
                  </span>
                  <ChevronDownIcon aria-hidden="true" className="size-4 text-gray-400" />
                </span>
              </MenuButton>
              <MenuItems
                transition
                className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-xl bg-white py-2 shadow-lg ring-1 ring-gray-900/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
              >
                <div className="px-3 py-2 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900 truncate">{userDisplayName}</p>
                  <p className="text-xs text-gray-500 truncate">{userRoleLabel}</p>
                </div>
                <MenuItem>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="w-full text-left flex items-center gap-2 mx-2 my-1 px-2 py-1.5 text-sm text-gray-700 rounded-md data-[focus]:bg-rose-50 data-[focus]:text-rose-700 data-[focus]:outline-none transition-colors"
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4" />
                    {t('sidebar.userMenu.signOut')}
                  </button>
                </MenuItem>
              </MenuItems>
            </Menu>
          </div>

          {/* Main */}
          <main className="bg-gray-50 min-h-[calc(100vh-3.5rem)]">
            {children}
          </main>
        </div>
      </div>
    </>
  )
}
